import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StepperCard } from '../../Card/StepperCard';
import { Button, ButtonOption } from '../../Fundamentals/Button';
import Spinner, { SpinnerPosition, SpinnerSize } from '../../Fundamentals/Spinner/Spinner';
import { StandardStepFooter, Step, StepBody } from '../../Fundamentals/Stepper/Step';
import PackageAudienceFields from './PackageAudienceFields';
import PackageBasicsFields from './PackageBasicsFields';
import PackageFlagToggles from './PackageFlagToggles';
import PackageOverviewTab from './PackageOverviewTab';
import { hasPackageDraftErrors, newPackageDraft, packageDraftErrors, toCreatePackageRequest, toPackagePreview } from './packageDraftUtils';
import type { CreatePackageRequest, PackageDraft, TargetGroupOption } from './Packages.types';

const FormColumn = ({ children }: { children: ReactNode }) => (
    <div className="row">
        <div className="col-12 col-lg-8">{children}</div>
    </div>
);

interface PackageCreateWizardProps {
    targetGroupOptions: TargetGroupOption[];
    isSaving: boolean;
    onCancel: () => void;
    onCreate: (model: CreatePackageRequest) => void;
}

const PackageCreateWizard = ({ targetGroupOptions, isSaving, onCancel, onCreate }: PackageCreateWizardProps) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState(newPackageDraft);
    const [showErrors, setShowErrors] = useState(false);
    const update = (change: Partial<PackageDraft>) => setDraft((prev) => ({ ...prev, ...change }));

    const errors = packageDraftErrors(draft);
    const hasErrors = hasPackageDraftErrors(errors);

    const create = () => {
        if (draft.type !== null) onCreate(toCreatePackageRequest({ ...draft, type: draft.type }));
    };

    return (
        <StepperCard allowBack={!isSaving}>
            <Step title={t('packages.basicDetails') ?? ''}>
                <StepBody>
                    <FormColumn>
                        <PackageBasicsFields draft={draft} errors={showErrors ? errors : {}} onChange={update} />
                    </FormColumn>
                </StepBody>
                <StandardStepFooter
                    onCancel={onCancel}
                    onNextClick={() => {
                        setShowErrors(hasErrors);
                        return !hasErrors;
                    }}
                />
            </Step>
            <Step title={t('packages.audiencesAndFlags') ?? ''}>
                <StepBody>
                    <FormColumn>
                        <PackageAudienceFields audiences={draft.audiences} targetGroupIds={draft.targetGroupIds} targetGroupOptions={targetGroupOptions} onChange={update} />
                        <div className="border-bottom my-24" />
                        <PackageFlagToggles flags={draft.flags} onChange={(flags) => update({ flags })} />
                    </FormColumn>
                </StepBody>
                <StandardStepFooter onCancel={onCancel} />
            </Step>
            <Step title={t('packages.reviewAndCreate') ?? ''}>
                <StepBody>
                    <div className="alert alert-info mb-16">{t('packages.createdAsDraft')}</div>
                    <PackageOverviewTab
                        vossPackage={toPackagePreview(draft, targetGroupOptions)}
                        selectedVersion={undefined}
                        canEdit={false}
                        onEdit={() => undefined}
                        hideCreated
                    />
                </StepBody>
                <StandardStepFooter onCancel={onCancel} isBusy={isSaving}>
                    {isSaving && <Spinner size={SpinnerSize.SMALL} position={SpinnerPosition.LEFT} />}
                    <Button option={ButtonOption.PRIMARY} disabled={isSaving} onClick={create}>
                        {t('packages.createPackage')}
                    </Button>
                </StandardStepFooter>
            </Step>
        </StepperCard>
    );
};

export default PackageCreateWizard;
